<template>
  <Combobox
    as="div"
    v-model="content"
    :nullable="nullable"
    :disabled="disabled || formDisabled || (resolvedOptions.length === 0 && !serverFiltering)"
    :multiple="Array.isArray(content)">
    <ComboboxLabel v-if="label" class="block text-sm font-medium leading-6 text-neutral-900 dark:text-white mb-2">
      {{ label }}
    </ComboboxLabel>
    <div class="flex">
      <div class="relative flex-1 mx-0">
        <ComboboxInput
          :placeholder="placeholder"
          class="w-full border-0 py-1.5 pl-3 shadow-xs ring-1 ring-inset focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6"
          :class="[classes, error ? 'pr-12' : 'pr-10', suffixIcon ? 'rounded-l-md' : 'rounded-md']"
          @change="query = $event.target.value"
          :display-value="(option) => selectedName" />
        <ComboboxButton
          class="absolute inset-y-0 right-0 flex items-center rounded-r-md px-4 cursor-pointer focus:outline-hidden">
          <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <i v-if="error" class="fa-sharp fa-exclamation-circle text-red-500 mr-2" aria-hidden="true"></i>
            <i
              v-if="modelValue && !disabled && !formDisabled"
              @click.stop="content = Array.isArray(content) ? [] : null"
              class="fa-sharp fa-regular fa-times text-neutral-400 hover:text-neutral-500 dark:hover:text-neutral-300 cursor-pointer mr-1 pointer-events-auto text-sm"
              aria-hidden="true"></i>
            <i
              class="fa-sharp fa-regular fa-chevron-down text-neutral-400 text-sm"
              aria-hidden="true"
              v-if="!serverFiltering || resolvedOptions.length > 0"></i>
          </span>
        </ComboboxButton>

        <ComboboxOptions
          v-if="filteredOptions.length > 0"
          class="absolute z-40 left-0 right-0 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-neutral-700 py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-hidden sm:text-sm">
          <ComboboxOption
            v-for="option in filteredOptions"
            :key="option[idKey]"
            :value="option[idKey]"
            as="template"
            v-slot="{ active, selected }">
            <li
              :class="[
                active ? 'bg-accent-600 text-white' : 'text-neutral-900 dark:text-neutral-300',
                'relative cursor-pointer select-none py-2 pl-8 pr-4'
              ]">
              <span :class="['block', selected && 'font-semibold']">
                {{ option[nameKey] }}
              </span>
              <span
                v-if="option.label"
                :class="['text-xs uppercase', active ? 'text-accent-200 dark:text-accent-400' : 'text-neutral-400']">
                {{ option.label }}
              </span>

              <span
                v-if="selected"
                :class="[
                  'absolute inset-y-0 left-0 flex items-center pl-1.5',
                  active ? 'text-white' : 'text-accent-600'
                ]">
                <i class="fa-sharp fa-regular fa-check" aria-hidden="true"></i>
              </span>
            </li>
          </ComboboxOption>
          <ComboboxOption v-if="query" :value="query" class="hidden"> {{ query }} </ComboboxOption>
        </ComboboxOptions>
      </div>

      <NtButton
        v-if="suffixIcon"
        level="primary"
        :icon="suffixIcon"
        class="rounded-l-none"
        :disabled="disabled || formDisabled"
        @click="$emit('suffix-click')" />
    </div>

    <TransitionGroup
      v-if="tags && tagButtons.length > 0"
      tag="div"
      class="flex flex-wrap gap-3 mt-3 empty:mt-0"
      move-class="transition-all duration-500"
      enter-active-class="transition-all duration-500"
      leave-active-class="transition-all duration-200 absolute"
      enter-from-class="scale-90 opacity-0"
      leave-to-class="scale-75 opacity-0">
      <NtButton
        v-for="tag in tagButtons"
        :key="tag[idKey]"
        icon="fa-times-circle"
        :disabled="disabled || formDisabled"
        @click="Array.isArray(content) ? $helpers.removeItem(tag[idKey], content) : (content = null)">
        {{ tag[nameKey] }}
      </NtButton>
    </TransitionGroup>

    <p v-if="error" class="mt-2 text-sm text-red-600" :id="hintId">
      {{ error }}
    </p>
    <p v-else-if="hint" class="mt-2 text-sm text-neutral-500 dark:text-neutral-400" :id="hintId">
      {{ hint }}
    </p>
  </Combobox>
</template>

<script>
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxLabel,
  ComboboxOption,
  ComboboxOptions
} from '@headlessui/vue'
import Str from 'laravel-js-str'
import NtButton from '../general/NtButton.vue'

export default {
  components: {
    Combobox,
    ComboboxButton,
    ComboboxInput,
    ComboboxLabel,
    ComboboxOption,
    ComboboxOptions,
    NtButton
  },
  emits: ['update:model-value', 'change', 'change-full', 'suffix-click'],
  inject: {
    form: {
      default: null
    },
    formDisabled: {
      default: false
    }
  },
  props: {
    label: {
      type: String,
      default: null
    },
    name: {
      type: String,
      default: null
    },
    placeholder: {
      type: String,
      default: 'None Selected'
    },
    hint: {
      type: String,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    modelValue: {
      default: null
    },
    options: {
      type: [Array, Object],
      required: true
    },
    idKey: {
      type: String,
      default: 'id'
    },
    nameKey: {
      type: String,
      default: 'name'
    },
    nullable: {
      type: Boolean,
      default: false
    },
    serverFiltering: {
      type: Boolean,
      default: false
    },
    allowCustom: {
      type: Boolean,
      default: false
    },
    wrap: {
      type: Boolean,
      default: false
    },
    maxResults: {
      type: Number,
      default: null
    },
    tags: {
      type: Boolean,
      default: false
    },
    suffixIcon: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      query: '',
      resolvedOptions: [],
      abortController: null,
      loading: false
    }
  },
  computed: {
    id() {
      return Str.random()
    },
    hintId() {
      return this.hint || this.error ? `${this.id}Hint` : null
    },
    classes() {
      if (this.error) {
        return 'bg-red-100 dark:bg-neutral-700 text-red-900 dark:text-neutral-100 ring-red-300 dark:ring-red-500 placeholder:text-red-300 focus:ring-red-500'
      }

      return 'bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 ring-neutral-300 dark:ring-neutral-600 placeholder:text-neutral-300 dark:placeholder:text-neutral-500 focus:ring-accent-600 dark:focus:ring-accent-500 disabled:cursor-not-allowed disabled:bg-neutral-50 disabled:text-neutral-500 disabled:ring-neutral-200'
    },
    error() {
      if (!this.form?.errors || !this.name) {
        return null
      }
      return this.form.errors[this.name]
    },
    content: {
      get() {
        return this.modelValue
      },
      set(value) {
        this.$emit('update:model-value', value)
        this.change()
        this.$emit('change', value)
        this.$nextTick(() => this.emitAll())
      }
    },
    selectedName() {
      if (Array.isArray(this.content)) {
        if (this.content.length === 0) {
          return null
        }
        if (this.tags) {
          return null
        }
        if (this.content.length !== 1) {
          return `${this.content.length} Selected`
        }

        const item =
          typeof this.resolvedOptions === 'object' && !Array.isArray(this.resolvedOptions)
            ? Object.values(this.resolvedOptions)
                .flat(1)
                .find((o) => (this.idKey ? o.id : o) === this.content[0])
            : this.resolvedOptions.find((o) => (this.idKey ? o.id : o) === this.content[0])
        return item ? item[this.nameKey] : this.allowCustom ? this.content : this.loading ? 'Loading...' : this.unknown
      }

      if (this.content === null) {
        return null
      }

      const item =
        typeof this.resolvedOptions === 'object' && !Array.isArray(this.resolvedOptions)
          ? Object.values(this.resolvedOptions)
              .flat(1)
              .find((o) => (this.idKey ? o.id : o) === this.content)
          : this.resolvedOptions.find((o) => (this.idKey ? o.id : o) === this.content)
      return item ? item[this.nameKey] : this.allowCustom ? this.content : this.loading ? 'Loading...' : this.unknown
    },
    filteredOptions() {
      if (this.serverFiltering) {
        return this.resolvedOptions
      }

      let filtered =
        this.query === ''
          ? this.resolvedOptions
          : this.resolvedOptions.filter((option) => {
              return option[this.nameKey].toLowerCase().includes(this.query.toLowerCase())
            })

      if (this.maxResults) {
        filtered = filtered.slice(0, this.maxResults)
      }

      const selected = this.modelValue
        ? this.resolvedOptions.filter((option) => {
            return (
              !filtered.find((f) => f[this.idKey] === option[this.idKey]) &&
              (Array.isArray(this.modelValue)
                ? this.modelValue.includes(option[this.idKey])
                : this.modelValue === option[this.idKey])
            )
          })
        : []

      return selected.concat(filtered)
    },
    tagButtons() {
      return this.resolvedOptions.filter((o) =>
        Array.isArray(this.content) ? this.content.includes(o[this.idKey]) : this.content === o[this.idKey]
      )
    }
  },
  methods: {
    change() {
      if (!this.form || !this.name) {
        return
      }
      this.form.validate(this.name)
    },
    emitAll() {
      const options = Array.isArray(this.resolvedOptions)
        ? this.resolvedOptions
        : Object.values(this.resolvedOptions).flat(1)

      this.$emit(
        'change-full',
        Array.isArray(this.content)
          ? options
              .filter((o) => this.content.includes(o[this.idKey]))
              .map((o) => o.name)
              .sort()
              .join(', ')
          : options.find((o) => o[this.idKey] === this.content) || null
      )
    },
    async fetchServerOptions() {
      this.abortController?.abort()
      if (!this.options?.endpoint) {
        this.resolvedOptions = []
        return true
      }

      this.abortController = new AbortController()
      try {
        const results = await this.$api.get(this.options.endpoint, {
          params: {
            ...this.options.filters,
            list: true
          },
          signal: this.abortController.signal
        })
        this.resolvedOptions = results.data
        return true
      } catch (e) {
        if (e.code !== 'ERR_CANCELED') {
          console.error(e)
        }
        return false
      }
    }
  },
  watch: {
    options: {
      async handler(newVal, oldVal) {
        if (JSON.stringify(newVal) === JSON.stringify(oldVal)) {
          return
        }
        if (typeof newVal === 'object' && !Array.isArray(newVal) && !newVal.endpoint) {
          this.resolvedOptions = newVal
          this.$nextTick(() => this.emitAll())
          return
        }
        if (Array.isArray(this.options)) {
          this.resolvedOptions = this.options
          return
        }

        this.loading = true
        const success = await this.fetchServerOptions()
        if (!success || this.allowCustom) {
          // Nothing more will arrive, so stop showing "Loading..." in place of the value.
          this.loading = false
          return
        }

        if (Array.isArray(this.content)) {
          const filtered = this.content.filter((o) => this.resolvedOptions.find((ro) => ro[this.idKey] === o))

          if (this.content.length !== filtered.length) {
            this.content = filtered
          }
        } else if (!this.resolvedOptions.find((o) => (this.idKey ? o[this.idKey] : o) === this.content)) {
          this.content = null
        }
        this.$nextTick(() => this.emitAll())
        this.loading = false
      },
      immediate: true
    },
    query(val) {
      if (this.allowCustom) {
        this.$emit('update:model-value', val)
        this.change()
      }
    }
  }
}
</script>
